import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { matchFaqs, matchesToContext } from '../utils/supportFaqMatch';

/** Netlify Function (same-origin). Override with VITE_SUPPORT_CHAT_URL if needed. */
function supportChatUrl(): string {
  const configured = import.meta.env.VITE_SUPPORT_CHAT_URL?.trim();
  if (configured) return configured.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/.netlify/functions/support-chat`;
  }
  return '/.netlify/functions/support-chat';
}

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
};

function shouldHideChat(pathname: string): boolean {
  return (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/admin-login') ||
    pathname.startsWith('/admin-setup')
  );
}

const QUICK_PROMPTS = [
  'How do I book a ride?',
  'Wheelchair accessible vehicles?',
  'Payment options',
  'Become a driver or partner',
];

const SupportChatWidget: React.FC = () => {
  const location = useLocation();
  const panelId = useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content:
        'Hello, I am AbleGo’s guided help assistant. I cannot complete a booking in chat, use Book a ride for that. How can I help?',
    },
  ]);

  const hidden = shouldHideChat(location.pathname);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
  }, [open, messages]);

  const appendAssistant = useCallback((content: string) => {
    setMessages((m) => [
      ...m,
      { id: crypto.randomUUID(), role: 'assistant', content },
    ]);
  }, []);

  const sendUserText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;

      setMessages((m) => [
        ...m,
        { id: crypto.randomUUID(), role: 'user', content: trimmed },
      ]);
      setDraft('');
      setBusy(true);

      try {
        const matches = matchFaqs(trimmed, 4);
        const faqContext = matchesToContext(matches, { maxAnswerChars: 360 });

        let data: { success?: boolean; reply?: string; error?: string } = {};
        try {
          const res = await fetch(supportChatUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: trimmed, faqContext }),
          });
          data = (await res.json()) as typeof data;
          if (!res.ok || data.success === false) {
            throw new Error(data.error || `HTTP ${res.status}`);
          }
        } catch {
          appendAssistant(
            matches.length
              ? `I can’t reach our assistant server right now. You might mean: "${matches[0].question}". See ablego.co.uk/faq or email hello@ablego.co.uk.`
              : 'Sorry — I couldn’t reach our assistant just now. Try ablego.co.uk/faq or hello@ablego.co.uk.',
          );
          return;
        }

        const reply =
          data.reply ||
          'No reply returned — please use our FAQ or contact page.';
        appendAssistant(reply);
      } finally {
        setBusy(false);
      }
    },
    [appendAssistant, busy],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (hidden) return null;

  return (
    <>
      {/* FAB: above theme toggle on narrow screens; sits to the left on md+ */}
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className="support-chat-fab fixed bottom-24 right-6 z-[45] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-600 to-blue-600 text-white shadow-xl ring-2 ring-white/30 transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-teal-400/60 focus:ring-offset-2 dark:focus:ring-offset-dark-900 md:bottom-6 md:right-[5.75rem]"
        title="AbleGo help chat"
      >
        {open ? <X className="h-7 w-7" aria-hidden /> : <MessageCircle className="h-7 w-7" aria-hidden />}
        <span className="sr-only">{open ? 'Close help chat' : 'Open help chat'}</span>
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-modal="true"
          aria-label="AbleGo help chat"
            className="support-chat-panel fixed z-[45] flex max-h-[min(72vh,520px)] w-[min(calc(100vw-2rem),22rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-dark-700 dark:bg-dark-800 bottom-44 right-4 md:bottom-24 md:right-[5.75rem]"
        >
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 bg-gradient-to-r from-teal-600 to-blue-600 px-4 py-3 text-white dark:border-dark-700">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-semibold tracking-tight">
                <Sparkles className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                AbleGo help
              </div>
              <p className="text-xs text-teal-50/95 mt-0.5 leading-snug">
                I am Here to help
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/70"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2 border-b border-gray-100 px-3 py-2 dark:border-dark-700">
            <Link
              to="/booking"
              className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/40"
              onClick={() => setOpen(false)}
            >
              Book ride
            </Link>
            <Link
              to="/faq"
              className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-900 hover:bg-teal-100 dark:bg-teal-950/50 dark:text-teal-100 dark:hover:bg-teal-900/40"
              onClick={() => setOpen(false)}
            >
              Full FAQ
            </Link>
            <Link
              to="/contact"
              className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-100 dark:hover:bg-dark-600"
              onClick={() => setOpen(false)}
            >
              Contact
            </Link>
          </div>

          <div
            ref={scrollRef}
            className="min-h-[200px] flex-1 space-y-3 overflow-y-auto px-3 py-3"
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[95%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900 dark:bg-dark-900 dark:text-gray-100'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-dark-900 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-100 px-3 py-2 dark:border-dark-700">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              Try asking
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  type="button"
                  disabled={busy}
                  onClick={() => sendUserText(q)}
                  className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:text-gray-200 dark:hover:bg-dark-700"
                >
                  {q}
                </button>
              ))}
            </div>

            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendUserText(draft);
                  }
                }}
                placeholder="Type your question…"
                className="min-h-[44px] flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30 dark:border-dark-600 dark:bg-dark-950 dark:text-gray-100"
              />
              <button
                type="button"
                disabled={busy || !draft.trim()}
                onClick={() => void sendUserText(draft)}
                className="mb-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Send message"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-[10px] leading-snug text-gray-500 dark:text-gray-400">
              Not for emergencies. Safeguarding or danger, call 999.{' '}
              <Link to="/faq" className="underline" onClick={() => setOpen(false)}>
                /faq
              </Link>
              .
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
